import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Switch } from '@/components/ui/switch';
import { ArrowLeftRight, X } from 'lucide-react';
import type {
  FluxogramaEdge as ApiEdge,
  FluxogramaEdgeCurva,
  FluxogramaEdgeTipo,
} from '@/types/fluxograma';

interface EdgePropertiesPanelProps {
  edge: ApiEdge;
  onChange: (updates: Partial<ApiEdge>) => void;
  onReverse: () => void;
  onClose: () => void;
  readonly?: boolean;
}

const LINHA_OPCOES: Array<{ value: FluxogramaEdgeTipo; label: string; dash: string }> = [
  { value: 'solid', label: 'Sólida', dash: '' },
  { value: 'dashed', label: 'Tracejada', dash: '6 4' },
  { value: 'dotted', label: 'Pontilhada', dash: '2 4' },
];

const CURVA_OPCOES: Array<{ value: FluxogramaEdgeCurva; label: string }> = [
  { value: 'bezier', label: 'Curva' },
  { value: 'step', label: 'Ângulos' },
  { value: 'straight', label: 'Reta' },
];

export function EdgePropertiesPanel({
  edge,
  onChange,
  onReverse,
  onClose,
  readonly,
}: EdgePropertiesPanelProps) {
  const [label, setLabel] = useState(edge.label ?? '');
  const [color, setColor] = useState(edge.color || '#64748b');

  useEffect(() => {
    setLabel(edge.label ?? '');
    setColor(edge.color || '#64748b');
  }, [edge.id, edge.label, edge.color]);

  return (
    <aside className="w-80 border-l bg-background flex flex-col">
      <header className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Conexão</div>
          <div className="text-sm font-semibold truncate max-w-[200px]">
            {edge.label || 'Conexão sem rótulo'}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <Label htmlFor="edge-label">Rótulo</Label>
          <Input
            id="edge-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => {
              const next = label.trim() || null;
              if (next !== edge.label) onChange({ label: next });
            }}
            placeholder="Ex.: Sim / Não"
            disabled={readonly}
          />
        </div>

        <div>
          <Label htmlFor="edge-color">Cor</Label>
          <div className="flex items-center gap-2 mt-1">
            <input
              id="edge-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              onBlur={() => onChange({ color })}
              disabled={readonly}
              className="h-9 w-12 rounded border bg-background cursor-pointer"
            />
            <Input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              onBlur={() => onChange({ color })}
              placeholder="#64748B"
              disabled={readonly}
              className="h-9"
            />
          </div>
        </div>

        <div>
          <Label className="mb-1.5 block">Estilo da linha</Label>
          <ToggleGroup
            type="single"
            value={edge.tipo}
            onValueChange={(v) => v && !readonly && onChange({ tipo: v as FluxogramaEdgeTipo })}
            className="justify-start gap-1"
          >
            {LINHA_OPCOES.map((opt) => (
              <ToggleGroupItem
                key={opt.value}
                value={opt.value}
                aria-label={opt.label}
                disabled={readonly}
                className="flex-1 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                title={opt.label}
              >
                <svg width="36" height="8" className="pointer-events-none">
                  <line
                    x1="2"
                    y1="4"
                    x2="34"
                    y2="4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray={opt.dash || undefined}
                  />
                </svg>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div>
          <Label className="mb-1.5 block">Formato</Label>
          <ToggleGroup
            type="single"
            value={edge.style_curva}
            onValueChange={(v) => v && !readonly && onChange({ style_curva: v as FluxogramaEdgeCurva })}
            className="justify-start gap-1"
          >
            {CURVA_OPCOES.map((opt) => (
              <ToggleGroupItem
                key={opt.value}
                value={opt.value}
                disabled={readonly}
                className="flex-1 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="space-y-2 pt-1">
          <Label className="block">Setas</Label>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Ponta no início</span>
            <Switch
              checked={edge.arrow_start}
              onCheckedChange={(v) => onChange({ arrow_start: v })}
              disabled={readonly}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Ponta no fim</span>
            <Switch
              checked={edge.arrow_end}
              onCheckedChange={(v) => onChange({ arrow_end: v })}
              disabled={readonly}
            />
          </div>
        </div>

        {!readonly && (
          <Button variant="outline" size="sm" className="w-full" onClick={onReverse}>
            <ArrowLeftRight className="h-4 w-4 mr-2" /> Inverter direção
          </Button>
        )}
      </div>
    </aside>
  );
}
